import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, Briefcase, Clock, Edit2, Save, Loader2, X, Camera, Flag } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SPECIALIZATIONS = [
  'Emergency Care',
  'Critical Care / ICU',
  'Pediatrics',
  'Oncology',
  'Surgery',
  'Mental Health',
  'Geriatrics',
  'Cardiology',
  'Labor & Delivery',
  'Home Health',
  'Other'
];

const ProfilePage = () => {
  const { userId } = useParams();
  const { user: currentUser, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [editData, setEditData] = useState({});

  const isOwnProfile = !userId || userId === currentUser?.id;

  useEffect(() => {
    fetchProfile();
  }, [userId, currentUser?.id]);

  const fetchProfile = async () => {
    try {
      if (isOwnProfile) {
        setProfile(currentUser);
        setEditData({
          name: currentUser?.name || '',
          specialization: currentUser?.specialization || '',
          experience_years: currentUser?.experience_years || '',
          bio: currentUser?.bio || ''
        });
      } else {
        const response = await axios.get(`${API}/api/users/profile/${userId}`);
        setProfile(response.data);
      }
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/users/profile`, {
        name: editData.name,
        specialization: editData.specialization || null,
        experience_years: editData.experience_years ? parseInt(editData.experience_years) : null,
        bio: editData.bio || null,
        avatar_url: editData.avatar_url || profile?.avatar_url || null
      });
      
      await refreshUser();
      setProfile({ ...profile, ...editData });
      setEditing(false);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, GIF, or WebP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/api/files/upload/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const newAvatarUrl = `${API}${response.data.url}`;
      setProfile({ ...profile, avatar_url: newAvatarUrl });
      setEditData({ ...editData, avatar_url: newAvatarUrl });
      await refreshUser();
      toast.success('Avatar updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F2]">
        <Header />
        <div className="flex justify-center items-center pt-32">
          <Loader2 className="h-8 w-8 animate-spin text-[#6B8E7B]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      <Header />
      
      <div className="content-container pt-20">
        <div className="max-w-2xl mx-auto py-8">
          <Card className="border-stone-200 rounded-2xl bg-white overflow-hidden">
            {/* Header */}
            <div className="h-32 bg-gradient-to-r from-[#6B8E7B] to-[#5A7868]" />
            
            <div className="px-6 pb-6 -mt-16">
              <div className="flex items-end justify-between mb-6">
                <div className="relative">
                  <Avatar className="h-28 w-28 border-4 border-white shadow-lg">
                    <AvatarImage src={profile?.avatar_url} />
                    <AvatarFallback className="bg-[#6B8E7B] text-white text-3xl">
                      {getInitials(profile?.name)}
                    </AvatarFallback>
                  </Avatar>
                  {isOwnProfile && (
                    <label className="absolute bottom-0 right-0 w-8 h-8 bg-[#6B8E7B] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#5A7868] transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        className="hidden"
                        disabled={uploadingAvatar}
                      />
                      {uploadingAvatar ? (
                        <Loader2 className="h-4 w-4 text-white animate-spin" />
                      ) : (
                        <Camera className="h-4 w-4 text-white" />
                      )}
                    </label>
                  )}
                </div>
                
                {isOwnProfile && !editing && (
                  <Button
                    variant="outline"
                    onClick={() => setEditing(true)}
                    className="rounded-full"
                    data-testid="edit-profile-button"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit Profile
                  </Button>
                )}
                
                {isOwnProfile && editing && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setEditing(false)}
                      className="rounded-full"
                      data-testid="cancel-edit-button"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      className="bg-[#6B8E7B] hover:bg-[#5A7868] rounded-full"
                      disabled={saving}
                      data-testid="save-profile-button"
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save
                    </Button>
                  </div>
                )}
              </div>

              {editing ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      className="rounded-xl"
                      data-testid="profile-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="specialization">Specialization</Label>
                    <Select
                      value={editData.specialization}
                      onValueChange={(value) => setEditData({ ...editData, specialization: value })}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="profile-specialization-select">
                        <SelectValue placeholder="Select your specialty" />
                      </SelectTrigger>
                      <SelectContent>
                        {SPECIALIZATIONS.map((spec) => (
                          <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="experience">Years of Experience</Label>
                    <Input
                      id="experience"
                      type="number"
                      min="0"
                      max="50"
                      value={editData.experience_years}
                      onChange={(e) => setEditData({ ...editData, experience_years: e.target.value })}
                      className="rounded-xl"
                      data-testid="profile-experience-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bio">Bio</Label>
                    <Textarea
                      id="bio"
                      value={editData.bio}
                      onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                      className="rounded-xl resize-none"
                      rows={4}
                      placeholder="Tell us about yourself..."
                      data-testid="profile-bio-input"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <h1 className="text-2xl font-bold text-stone-800 font-['Outfit']">{profile?.name}</h1>
                    {profile?.is_premium && <span className="premium-badge">Premium</span>}
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-stone-600 mb-6">
                    {profile?.specialization && (
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-[#6B8E7B]" />
                        <span>{profile.specialization}</span>
                      </div>
                    )}
                    {profile?.experience_years && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-[#6B8E7B]" />
                        <span>{profile.experience_years} years experience</span>
                      </div>
                    )}
                  </div>

                  {profile?.bio && (
                    <div className="bg-stone-50 rounded-xl p-4">
                      <h3 className="text-sm font-medium text-stone-500 mb-2">About</h3>
                      <p className="text-stone-700 leading-relaxed">{profile.bio}</p>
                    </div>
                  )}

                  {!profile?.bio && isOwnProfile && (
                    <div className="bg-stone-50 rounded-xl p-4 text-center">
                      <p className="text-stone-500">Add a bio to tell others about yourself</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>

          {/* Stats Card */}
          <Card className="border-stone-200 rounded-2xl bg-white mt-6">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-stone-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-[#6B8E7B]">0</p>
                  <p className="text-sm text-stone-500">Posts</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-[#6B8E7B]">0</p>
                  <p className="text-sm text-stone-500">Comments</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
